/**
 * UI Foundation Stubs - Temporary implementations
 * 
 * These are placeholder implementations for missing @ferp/ui-foundation package
 * components until the full design system is implemented.
 */

import React from 'react';

export type CriticalityLevel = 'low' | 'medium' | 'high' | 'critical' | 'life-critical';

export interface BaseComponentProps {
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  'data-testid'?: string;
}

export interface FerpButtonProps extends BaseComponentProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'subtle';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}

export const FerpButton: React.FC<FerpButtonProps> = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  className,
  ...props 
}) => {
  // Temporary implementation using basic button
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`ferp-button ferp-button--${variant} ferp-button--${size} ${className || ''}`}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
};

export const getCriticalityColor = (level: CriticalityLevel): string => {
  const colors = {
    'low': '#22c55e',
    'medium': '#f59e0b', 
    'high': '#ef4444',
    'critical': '#dc2626',
    'life-critical': '#991b1b'
  };
  return colors[level] || colors.medium;
};

// Placeholder exports for other foundation components
export const FerpCard = ({ children, ...props }: { children: React.ReactNode } & BaseComponentProps) => (
  <div className="ferp-card" {...props}>{children}</div>
);

export const FerpPanel = ({ children, ...props }: { children: React.ReactNode } & BaseComponentProps) => (
  <div className="ferp-panel" {...props}>{children}</div>
);

export interface FerpTextProps extends BaseComponentProps {
  children: React.ReactNode;
  /** Legacy size prop; prefer textSize */
  size?: string;
  /** Critical text sizing tokens */
  textSize?: string;
  weight?: string;
  color?: string;
  criticality?: string;
  isLifeCritical?: boolean;
}

export const FerpText: React.FC<FerpTextProps> = ({ children, size, textSize, weight, color, ...props }) => (
  <span
    className="ferp-text"
    style={{ fontSize: textSize || size, fontWeight: weight as any, color, ...(props.style || {}) }}
    {...props}
  >
    {children}
  </span>
);